import { VirtualGeometryResolver } from './geometry-resolvers/virtual-geometry-resolver';
import { HighlightsRenderer } from './highlights-renderer';
import { TABLE_HIGHLIGHTS_RESIZE_OBSERVE_ELEMENT_ID } from './table-resize-observe-target';
import { type GeometryResolver } from './types';

export interface HighlightsControllerOptions {
  id: string;
}

export class HighlightsController {
  #container: HTMLElement;

  #layers = new Set<HighlightsRenderer>();
  #resolver: GeometryResolver;

  // Track the observer and the width internally
  #resizeObserver: ResizeObserver | null = null;
  #lastWidth: number | null = null;

  constructor(resolver: GeometryResolver, options: HighlightsControllerOptions) {
    this.#resolver = resolver;

    this.#container = document.createElement('div');
    this.#container.id = options.id;

    this.#container.style.position = 'absolute';
    this.#container.style.top = '0';
    this.#container.style.left = '0';
    this.#container.style.pointerEvents = 'none';
  }

  /**
   * Attaches the highlights container to the scroll container.
   * `createLayer` calls this automatically when a scroll parent exists, using
   * the default resize observe target when present. Use `mount` explicitly
   * only when you need a non-default `resizeTarget`.
   *
   * @param resizeTarget Optional. The specific DOM element to observe for width changes.
   * Defaults to the scroll container if not provided.
   */
  public mount(resizeTarget?: HTMLElement | null) {
    const parent = this.#resolver.getScrollContainer();

    if (parent) {
      if (this.#container.parentElement === parent) {
        return;
      }

      parent.style.position = 'relative';
      parent.appendChild(this.#container);

      // Autonomous Resize Management
      const targetToObserve = resizeTarget || parent;

      this.#lastWidth = targetToObserve.clientWidth;

      // Disconnect any existing observer just in case mount() is called multiple times
      if (this.#resizeObserver) {
        this.#resizeObserver.disconnect();
      }

      this.#resizeObserver = new ResizeObserver(() => {
        const newWidth = targetToObserve.clientWidth;

        // Only clear cache if the width changed (Column Resize)
        if (newWidth !== this.#lastWidth) {
          this.#lastWidth = newWidth;
          this.clearVirtualGeometryCache();
        }

        // Tell all registered layers to redraw to stick to the moving cells
        this.#layers.forEach((layer) => layer.redrawAll());
      });

      this.#resizeObserver.observe(targetToObserve);
    }
  }

  #defaultResizeObserveTarget(): HTMLElement | null {
    if (typeof document === 'undefined') {
      return null;
    }

    return document.getElementById(TABLE_HIGHLIGHTS_RESIZE_OBSERVE_ELEMENT_ID);
  }

  #ensureMountedBeforeLayer() {
    const parent = this.#resolver.getScrollContainer();

    if (!parent) {
      return;
    }

    if (this.#container.parentElement === parent) {
      return;
    }

    this.mount(this.#defaultResizeObserveTarget());
  }

  /**
   * Factory: Creates a rendering layer. Ensures the overlay root is mounted
   * when the geometry resolver reports a scroll container (same behavior as
   * the previous `mount` + `createLayer` sequence in selection effects).
   */
  public createLayer(): HighlightsRenderer {
    this.#ensureMountedBeforeLayer();

    const layer = new HighlightsRenderer(this.#container, this.#resolver);

    this.#layers.add(layer);

    return layer;
  }

  /**
   * Safely destroys a specific layer and removes its DOM elements.
   */
  public removeLayer(layer: HighlightsRenderer) {
    if (this.#layers.has(layer)) {
      this.#layers.delete(layer);
    }

    layer.dispose();

    if (this.#layers.size === 0) {
      this.#detachResizeObserverAndContainer();
    }
  }

  /**
   * Completely tears down the controller, destroying all layers,
   * disconnecting observers, and removing the root container from the DOM.
   */
  public dispose() {
    this.#layers.forEach((l) => l.dispose());
    this.#layers.clear();
    this.#detachResizeObserverAndContainer();
  }

  #detachResizeObserverAndContainer() {
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }

    if (this.#container.parentElement) {
      this.#container.parentElement.removeChild(this.#container);
    }
  }

  /**
   * Returns the scroll container which is possibly null if the react app
   * has not mounted.
   */
  public getScrollContainer(): HTMLElement | null {
    return this.#resolver.getScrollContainer();
  }

  /**
   * Clears phantom geometry caches when the injected resolver is a
   * `VirtualGeometryResolver`. No-op for other implementations.
   */
  public clearVirtualGeometryCache() {
    if (this.#resolver instanceof VirtualGeometryResolver) {
      this.#resolver.clearCache();
    }
  }

  /**
   * Returns the highlights container.
   */
  public getContainer(): HTMLElement {
    return this.#container;
  }
}
